/*
 * The opening (CLAUDE.md §4 appearance, Saif 21 Oct 2026): the launch screen's small centred mark lifts and glides to
 * its home in the Home header while the page fades in beneath it (about a second, once per cold start), and the first
 * time ever a welcome holds the mark, shows a tagline and a few cards. The rules here are pure, so they're tested; the
 * drawing is components/Intro.tsx.
 */

export const WELCOME_SEEN_KEY = 'tikkas-welcome-seen';

/** How long the glide takes, in ms: the mark holds, then travels while the page fades in. */
export const INTRO_HOLD_MS = 420;
export const INTRO_GLIDE_MS = 860;
export const INTRO_FADE_MS = 360;
/** After the hold, the longest the mark waits for the page beneath to settle (the account, the fonts, the safe area) before it glides anyway. */
export const INTRO_SETTLE_CAP_MS = 1500;
/** The welcome's beats: the tagline after the mark settles, the cards after the tagline. */
export const WELCOME_TAGLINE_AT_MS = 500;
export const WELCOME_CARDS_AT_MS = 1500;

export type IntroKind = 'glide' | 'fade' | 'none';

/**
 * Which opening to play: the glide once per cold start with motion on; a plain fade under Reduce Motion; nothing when the
 * theme's motion is off or this isn't a cold start (a tab change or a resume never replays it).
 */
export function introKind(o: { motion: boolean; reducedMotion: boolean; coldStart: boolean }): IntroKind {
  if (!o.coldStart || !o.motion) return 'none';
  return o.reducedMotion ? 'fade' : 'glide';
}

/**
 * Whether the welcome is due: switched on with at least one card, and not seen on this phone or browser (the local
 * flag) nor on the account (the server's flag, null while signed out or not yet loaded). Any of Skip, Browse or Join
 * marks it seen for good.
 */
export function welcomeDue(o: { enabled: boolean; cards: number; localSeen: boolean; serverSeen: boolean | null }): boolean {
  return o.enabled && o.cards > 0 && !o.localSeen && !o.serverSeen;
}

/** What to do about the server's flag once the account loads: tell the server, remember locally, or nothing. */
export function welcomeSync(o: { localSeen: boolean; serverSeen: boolean | null }): 'tell_server' | 'remember' | 'nothing' {
  if (o.serverSeen === null) return 'nothing';
  if (o.localSeen && !o.serverSeen) return 'tell_server';
  if (!o.localSeen && o.serverSeen) return 'remember';
  return 'nothing';
}

/** A count-up's value at `t` of the way through (0 to 1), easing out, always landing on the target exactly. */
export function countUpValue(target: number, t: number): number {
  if (t >= 1) return target;
  if (t <= 0) return 0;
  const eased = 1 - Math.pow(1 - t, 3);
  return Math.round(target * eased);
}

/** The transform that moves a mark drawn at `from` onto `to` (both viewport rectangles), scaling to the target's width. */
export function glideTransform(from: { left: number; top: number; width: number }, to: { left: number; top: number; width: number }): string {
  const k = from.width > 0 ? to.width / from.width : 1;
  return `translate(${(to.left - from.left).toFixed(1)}px, ${(to.top - from.top).toFixed(1)}px) scale(${k.toFixed(4)})`;
}

export const readLocalSeen = (): boolean => {
  try {
    return localStorage.getItem(WELCOME_SEEN_KEY) === '1';
  } catch {
    return false;
  }
};
export const rememberSeen = (): void => {
  try {
    localStorage.setItem(WELCOME_SEEN_KEY, '1');
  } catch {
    /* private mode: it shows again next time, which is harmless */
  }
};
